import {useMemo} from 'react'

import {filterConversationItems, filterThreads} from './utils/filter-items'
import {buildConversationItems} from './utils/build-conversation-items'

import {usePullRequestBasePath, useThreads} from '../../stores/layout-selectors'
import {sortConversationItemsByDate} from './utils/sort-items'
import {CommentsSort} from './use-comments-sort-state'
import type {CommentsFilterState} from './use-comments-filter-state'
import {useInfiniteConversationCommentsQuery} from '../../page-data/loaders/use-conversation-comments'

export interface UseFilteredCommentsDataOptions {
  /** Current filter state. */
  filterState: CommentsFilterState
  /** Current sort direction. */
  sort: CommentsSort
}

const emptyItems = {
  unfilteredItems: [],
  filteredSortedItems: [],
}

/** Fetch and calculate filtered conversation items (issue comments and review summaries) with filtered threads in reviews. */
export function useFilteredConversationItems({filterState, sort}: UseFilteredCommentsDataOptions) {
  const threadPreviews = useThreads()

  const basePath = usePullRequestBasePath()

  const query = useInfiniteConversationCommentsQuery({
    basePath,
  })
  const conversationComments = query.data?.comments

  // Conversation mode displays full list of comments and reviews with threads
  const conversationItems = useMemo(() => {
    // Diff-order sort is not supported for conversation mode
    if (sort === 'diff-order') return emptyItems

    // Items are built using filtered threads so that we don't show non-matching threads inside matching review items
    const threadsForItems = filterThreads(threadPreviews, filterState)

    const unfilteredItems = buildConversationItems(conversationComments ?? [], threadPreviews)
    const filterableItems = buildConversationItems(conversationComments ?? [], threadsForItems)
    const filteredSortedItems = sortConversationItemsByDate(filterConversationItems(filterableItems, filterState), sort)

    return {unfilteredItems, filteredSortedItems}
  }, [threadPreviews, filterState, conversationComments, sort])

  return {
    query,
    /** Conversation items matching the conversation filter and sort direction. */
    filteredConversationItems: conversationItems.filteredSortedItems,
    /** Total number of conversation items in this PR. */
    totalConversationItemsCount: conversationItems.unfilteredItems.length,
  }
}
